import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import type { Editor } from '@tiptap/react';
import { type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import {
    ArrowDownIcon,
    ArrowUpIcon,
    CaseSensitiveIcon,
    RegexIcon,
    Repeat2Icon,
    ReplaceAllIcon,
    ReplaceIcon,
    SearchIcon,
    WholeWordIcon,
    XIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

type SearchSnapshot = {
    totalResults: number;
    resultIndex: number;
    currentFrom: number | null;
    currentIsReplaceable: boolean;
    allResultsAreNotReplaceable: boolean;
};

function searchSnapshotEquality(a: SearchSnapshot, b: SearchSnapshot | null): boolean {
    return (
        a.totalResults === b?.totalResults &&
        a.resultIndex === b?.resultIndex &&
        a.currentFrom === b?.currentFrom &&
        a.currentIsReplaceable === b?.currentIsReplaceable &&
        a.allResultsAreNotReplaceable === b?.allResultsAreNotReplaceable
    );
}

export function SearchReplacePopover({ isSplitView }: { isSplitView: boolean }) {
    const { editor } = useTiptapEditor();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [useRegex, setUseRegex] = useState(false);
    const [showReplaceContainer, setShowReplaceContainer] = useState(false);

    const searchState = useEditorState({
        editor,
        selector: useCallback((ctx: EditorStateSnapshot<Editor | null>): SearchSnapshot => {
            const storage = ctx.editor?.storage.zySearchAndReplace;
            const resultIndex = storage?.resultIndex ?? 0;
            return {
                totalResults: storage?.results.length ?? 0,
                resultIndex,
                currentFrom: storage?.results[resultIndex]?.from ?? null,
                currentIsReplaceable: storage?.results[resultIndex]?.isReplaceable ?? false,
                allResultsAreNotReplaceable: !storage?.hasReplaceableMatch,
            };
        }, []),
        equalityFn: searchSnapshotEquality,
    });

    const totalResults = searchState?.totalResults ?? 0;
    const resultIndex = searchState?.resultIndex ?? 0;
    const currentResult = totalResults > 0 ? resultIndex + 1 : 0;
    const currentIsReplaceable = searchState?.currentIsReplaceable ?? false;
    const allResultsAreNotReplaceable = searchState?.allResultsAreNotReplaceable ?? false;

    const replaceTooltip = totalResults === 0 ? 'No search results' : !currentIsReplaceable ? 'This match cannot be replaced' : 'Replace';
    const replaceAllTooltip =
        totalResults === 0 ? 'No search results' : allResultsAreNotReplaceable ? 'None of the matches can be replaced' : 'Replace all';

    const onSetReplaceTerm = useCallback(
        (term: string) => {
            setReplaceTerm(term);
            editor?.commands.setReplaceTerm(term);
        },
        [editor, setReplaceTerm],
    );

    const onSetCaseSensitive = useCallback(
        (caseSensitive: boolean) => {
            setCaseSensitive(caseSensitive);
            editor?.commands.setCaseSensitive(caseSensitive);
            editor?.commands.resetIndex();
        },
        [editor, setCaseSensitive],
    );

    const onSetWholeWord = useCallback(
        (wholeWord: boolean) => {
            setWholeWord(wholeWord);
            editor?.commands.setWholeWord(wholeWord);
            editor?.commands.resetIndex();
        },
        [editor, setWholeWord],
    );

    const onSetUseRegex = useCallback(
        (useRegex: boolean) => {
            setUseRegex(useRegex);
            editor?.commands.setRegex(useRegex);
            editor?.commands.resetIndex();
        },
        [editor, setUseRegex],
    );

    const onReplace = useCallback(() => {
        editor?.commands.replace();
    }, [editor]);

    const onReplaceAll = useCallback(() => {
        editor?.commands.replaceAll();
        editor?.commands.resetIndex();
    }, [editor]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (isSplitView) return;
            if (nextOpen === open) return;
            setOpen(nextOpen);

            if (nextOpen) {
                editor?.commands.setSearchTerm(searchTerm);
                editor?.commands.setReplaceTerm(replaceTerm);
            }

            if (!nextOpen) {
                editor?.commands.setSearchTerm('');
                editor?.commands.setReplaceTerm('');
                editor?.commands.resetIndex();
            }
        },
        [editor, isSplitView, open, replaceTerm, searchTerm],
    );

    const handleOpenChangeRef = useRef(handleOpenChange);
    useEffect(() => {
        handleOpenChangeRef.current = handleOpenChange;
    }, [handleOpenChange]);

    useEffect(() => {
        const handleSearchShortcut = (event: KeyboardEvent) => {
            if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
            if (event.key !== 'f' && event.key !== 'F') return;

            event.preventDefault();
            handleOpenChangeRef.current(true);
        };

        document.addEventListener('keydown', handleSearchShortcut, true);
        return () => document.removeEventListener('keydown', handleSearchShortcut, true);
    }, []);

    useEffect(() => {
        return () => {
            if (!editor || editor.isDestroyed) return;
            editor.commands.setSearchTerm('');
            editor.commands.setReplaceTerm('');
            editor.commands.resetIndex();
        };
    }, [editor]);

    if (!editor) return null;

    return (
        <Popover
            open={open}
            onOpenChange={handleOpenChange}
        >
            <PopoverTrigger asChild>
                <Button
                    data-style="ghost"
                    tooltip="Search and replace"
                    disabled={isSplitView}
                >
                    <SearchIcon className="w-4 h-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0.5 w-full"
                align="end"
            >
                {/* Search bar container */}
                <div className="flex flex-row gap-1 items-center">
                    <Input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={e => {
                            const term = e.target.value;
                            setSearchTerm(term);
                            editor?.commands.setSearchTerm(term);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (e.shiftKey) {
                                    editor.commands.previousSearchResult();
                                } else {
                                    editor.commands.nextSearchResult();
                                }
                            }
                        }}
                        className="w-48 h-8 border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    {/* Search options */}
                    <div className="flex flex-row gap-0 items-center">
                        <Button
                            size="small"
                            data-style="ghost"
                            data-active-state={caseSensitive ? 'on' : 'off'}
                            tooltip="Match case"
                            onClick={() => onSetCaseSensitive(!caseSensitive)}
                        >
                            <CaseSensitiveIcon className="w-4 h-4" />
                        </Button>
                        <Button
                            size="small"
                            data-style="ghost"
                            data-active-state={wholeWord ? 'on' : 'off'}
                            tooltip="Match whole word"
                            onClick={() => onSetWholeWord(!wholeWord)}
                        >
                            <WholeWordIcon className="w-4 h-4" />
                        </Button>
                        <Button
                            size="small"
                            data-style="ghost"
                            data-active-state={useRegex ? 'on' : 'off'}
                            tooltip="Use regular expressions"
                            onClick={() => onSetUseRegex(!useRegex)}
                        >
                            <RegexIcon className="w-4 h-4" />
                        </Button>
                    </div>
                    {/* Number of results */}
                    <div>
                        <p className="text-xs text-gray-500 text-nowrap">
                            {currentResult} of {totalResults}
                        </p>
                    </div>
                    {/* Up and down buttons */}
                    <div className="flex flex-row gap-0 items-center">
                        <Button
                            size="small"
                            data-style="ghost"
                            tooltip="Go to previous result"
                            disabled={totalResults === 0}
                            onClick={() => editor.commands.previousSearchResult()}
                        >
                            <ArrowUpIcon className="w-4 h-4" />
                        </Button>
                        <Button
                            size="small"
                            data-style="ghost"
                            tooltip="Go to next result"
                            disabled={totalResults === 0}
                            onClick={() => editor.commands.nextSearchResult()}
                        >
                            <ArrowDownIcon className="w-4 h-4" />
                        </Button>
                    </div>
                    {/* Replace button */}
                    <Button
                        size="small"
                        data-style="ghost"
                        data-active-state={showReplaceContainer ? 'on' : 'off'}
                        onClick={() => setShowReplaceContainer(!showReplaceContainer)}
                        tooltip="Open replace modal"
                    >
                        <Repeat2Icon className="w-4 h-4" />
                    </Button>
                    {/* Close button to close the search popover */}
                    <Button
                        size="small"
                        data-style="ghost"
                        tooltip="Close"
                        onClick={() => {
                            handleOpenChange(false);
                        }}
                    >
                        <XIcon className="w-4 h-4" />
                    </Button>
                </div>
                {/* Replace container */}
                {showReplaceContainer && (
                    <div className="flex flex-row gap-1 items-center">
                        {/* Replace with */}
                        <Input
                            type="text"
                            placeholder="Replace with"
                            value={replaceTerm}
                            onChange={e => onSetReplaceTerm(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onReplace();
                                }
                            }}
                            className="w-48 h-8 border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                        <div className="flex flex-row gap-0 items-center">
                            {/* Replace button */}
                            <Button
                                size="small"
                                data-style="ghost"
                                tooltip={replaceTooltip}
                                disabled={totalResults === 0 || !currentIsReplaceable}
                                onClick={onReplace}
                            >
                                <ReplaceIcon className="w-4 h-4" />
                            </Button>
                            {/* Replace all button */}
                            <Button
                                size="small"
                                data-style="ghost"
                                tooltip={replaceAllTooltip}
                                disabled={totalResults === 0 || allResultsAreNotReplaceable}
                                onClick={onReplaceAll}
                            >
                                <ReplaceAllIcon className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
